/**
 * Firebase Auth Provider Component
 * LegalSyncUp - Swiss Legal Compliance Platform
 *
 * FIREBASE AUTH - ENTERPRISE IDENTITY PROVIDER
 * =============================================
 * React context provider for Firebase Authentication with Identity Platform.
 * Provides enterprise-grade identity management for multi-tenant scenarios.
 *
 * FEATURES:
 * - Unified auth interface compatible with AuthContext
 * - Automatic session management and token refresh
 * - Support for OAuth providers and enterprise SSO
 * - Multi-tenant identity management
 *
 * ACTIVATION: Set VITE_USE_FIREBASE_AUTH=true
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithEmail,
  signUpWithEmail,
  signOut as firebaseSignOut,
  signInWithGoogle,
  signInWithGitHub,
  resetPassword,
  onAuthChange,
  getIdToken,
} from './auth';
import { isFirebaseConfigured } from './config';

// Unified user type compatible with existing auth systems
interface AuthUser {
  id: string;
  email: string | null;
  name?: string | null;
  emailVerified?: boolean;
  image?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface RateLimitInfo {
  remainingAttempts: number;
  lockedUntil: Date | null;
}

interface FirebaseAuthContextValue {
  user: AuthUser | null;
  firebaseUser: User | null;
  session: { token: string | null } | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  rateLimitInfo: RateLimitInfo | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null; rateLimited?: boolean }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signInWithGitHub: () => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  refresh: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  checkAccountLockout: (email: string) => Promise<{ locked: boolean; lockExpiresAt: Date | null }>;
}

const FirebaseAuthContext = createContext<FirebaseAuthContextValue | undefined>(undefined);

/**
 * Hook to access Firebase Auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useFirebaseAuth = (): FirebaseAuthContextValue => {
  const ctx = useContext(FirebaseAuthContext);
  if (!ctx) {
    throw new Error('useFirebaseAuth must be used within FirebaseAuthProvider');
  }
  return ctx;
};

/**
 * Convert Firebase User to unified AuthUser format
 */
const convertToAuthUser = (firebaseUser: User | null): AuthUser | null => {
  if (!firebaseUser) return null;

  return {
    id: firebaseUser.uid,
    email: firebaseUser.email,
    name: firebaseUser.displayName,
    emailVerified: firebaseUser.emailVerified,
    image: firebaseUser.photoURL,
    createdAt: firebaseUser.metadata.creationTime,
    updatedAt: firebaseUser.metadata.lastSignInTime,
  };
};

interface FirebaseAuthProviderProps {
  children: React.ReactNode;
}

/**
 * Firebase Auth Provider Component
 */
export const FirebaseAuthProvider: React.FC<FirebaseAuthProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Subscribe to auth state changes
  useEffect(() => {
    if (!isFirebaseConfigured()) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthChange(async (user) => {
      setFirebaseUser(user);

      // Get ID token for API calls
      if (user) {
        const idToken = await getIdToken();
        setToken(idToken);
      } else {
        setToken(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Convert to unified user format
  const user = useMemo(() => convertToAuthUser(firebaseUser), [firebaseUser]);

  // Session object containing token
  const session = useMemo(() => (token ? { token } : null), [token]);

  // Sign in with email/password
  const handleSignIn = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const result = await signInWithEmail(email, password);
      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }
      return { error: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign up with email/password
  const handleSignUp = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const result = await signUpWithEmail(email, password);
      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }
      return { error: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out
  const handleSignOut = useCallback(async () => {
    setError(null);
    setRateLimitInfo(null);
    await firebaseSignOut();
  }, []);

  // Sign in with Google
  const handleGoogleSignIn = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }
      return { error: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign in with GitHub
  const handleGitHubSignIn = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const result = await signInWithGitHub();
      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }
      return { error: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password
  const handleResetPassword = useCallback(async (email: string) => {
    setError(null);

    const result = await resetPassword(email);
    if (result.error) {
      setError(result.error);
    }
    return result;
  }, []);

  // Refresh session (get new token)
  const handleRefresh = useCallback(async () => {
    if (firebaseUser) {
      const newToken = await getIdToken(true);
      setToken(newToken);
    }
  }, [firebaseUser]);

  // Get ID token
  const handleGetIdToken = useCallback(async (forceRefresh = false) => {
    return getIdToken(forceRefresh);
  }, []);

  // Account lockout check (Firebase handles this internally)
  const checkAccountLockout = useCallback(async (_email: string) => {
    return { locked: false, lockExpiresAt: null };
  }, []);

  const value = useMemo<FirebaseAuthContextValue>(() => ({
    user,
    firebaseUser,
    session,
    loading,
    error,
    isAuthenticated: !!user,
    rateLimitInfo,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    signInWithGoogle: handleGoogleSignIn,
    signInWithGitHub: handleGitHubSignIn,
    resetPassword: handleResetPassword,
    refresh: handleRefresh,
    getIdToken: handleGetIdToken,
    checkAccountLockout,
  }), [
    user,
    firebaseUser,
    session,
    loading,
    error,
    rateLimitInfo,
    handleSignIn,
    handleSignUp,
    handleSignOut,
    handleGoogleSignIn,
    handleGitHubSignIn,
    handleResetPassword,
    handleRefresh,
    handleGetIdToken,
    checkAccountLockout,
  ]);

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
};

export default FirebaseAuthProvider;
